import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminAuthProvider, useAdminAuth } from './context/AdminAuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SiteSettingsProvider } from './context/SiteSettingsContext';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// User / Provider pages
import UserRegister      from './pages/UserRegister';
import UserLogin         from './pages/UserLogin';
import ForgotPassword    from './pages/ForgotPassword';
import ResetPassword     from './pages/ResetPassword';
import ProviderRegister  from './pages/ProviderRegister';
import ProviderLogin     from './pages/ProviderLogin';
import AgentLogin        from './pages/AgentLogin';
import UserDashboard     from './pages/UserDashboard';
import ProviderDashboard from './pages/ProviderDashboard';
import AssetManagement   from './pages/AssetManagement';
import QRProfile         from './pages/QRProfile';
import PublicProfile     from './pages/PublicProfile';
import PetPublicProfile  from './pages/PetPublicProfile';
import FamilyAcceptPage from './pages/FamilyAcceptPage';
import Home              from './pages/Home';
import WholesaleCatalog  from './pages/WholesaleCatalog';
import Navbar            from './components/Navbar';

// Admin pages
import AdminLogin     from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsers     from './pages/admin/AdminUsers';
import AdminProviders from './pages/admin/AdminProviders';
import AdminAssets    from './pages/admin/AdminAssets';
import AdminInventory from './pages/admin/AdminInventory';
import AdminManagement from './pages/admin/AdminManagement';
import AdminSettings   from './pages/admin/AdminSettings';
import AdminWholesale  from './pages/admin/AdminWholesale';
import AdminAppearance from './pages/admin/AdminAppearance';

import './styles/App.css';

// ── Guards ────────────────────────────────────────────────────

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading…</div>;
  if (!user)   return <Navigate to="/" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role))
    return <Navigate to="/" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { admin, loading } = useAdminAuth();
  if (loading)
    return (
      <div className="loading" style={{ background: '#0d0f14', color: '#e8eaf2', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        Loading…
      </div>
    );
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
};

// ── Route tree ────────────────────────────────────────────────
// All routes live in ONE flat <Routes> so there is no prefix
// stripping or nested-router confusion.
// Admin paths render their own layout (no Navbar).
// Everything else gets the Navbar wrapper.

function AppRoutes() {
  return (
    <Routes>

      {/* ── Admin (no Navbar) ───────────────────────────────── */}
      <Route path="/admin/login"     element={<AdminLogin />} />
      <Route path="/admin"           element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/users"     element={<AdminRoute><AdminUsers /></AdminRoute>} />
      <Route path="/admin/providers" element={<AdminRoute><AdminProviders /></AdminRoute>} />
      <Route path="/admin/assets"     element={<AdminRoute><AdminAssets /></AdminRoute>} />
      <Route path="/admin/inventory"  element={<AdminRoute><AdminInventory /></AdminRoute>} />
      <Route path="/admin/admins"     element={<AdminRoute><AdminManagement /></AdminRoute>} />
      <Route path="/admin/settings"   element={<AdminRoute><AdminSettings /></AdminRoute>} />
      <Route path="/admin/wholesale"  element={<AdminRoute><AdminWholesale /></AdminRoute>} />
      <Route path="/admin/appearance" element={<AdminRoute><AdminAppearance /></AdminRoute>} />

      {/* ── Everything else (with Navbar) ───────────────────── */}
      <Route path="/*" element={
        <>
          <Navbar />
          <Routes>
            <Route path="/"                  element={<Home />} />

            {/* Auth */}
            <Route path="/register/user"     element={<UserRegister />} />
            <Route path="/login/user"        element={<UserLogin />} />
            <Route path="/forgot-password"   element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/family/accept/:token"  element={<ProtectedRoute allowedRoles={['user']}><FamilyAcceptPage /></ProtectedRoute>} />

            {/* Provider */}
            <Route path="/register/provider" element={<ProviderRegister />} />
            <Route path="/login/provider"    element={<ProviderLogin />} />
            <Route path="/login/agent"       element={<AgentLogin />} />

            {/* Protected */}
            <Route path="/dashboard"         element={<ProtectedRoute allowedRoles={['user']}><UserDashboard /></ProtectedRoute>} />
            <Route path="/provider/dashboard" element={<ProtectedRoute allowedRoles={['provider','agent']}><ProviderDashboard /></ProtectedRoute>} />
            <Route path="/provider/assets"   element={<ProtectedRoute allowedRoles={['provider']}><AssetManagement /></ProtectedRoute>} />
            <Route path="/profile/:qrCodeId" element={<ProtectedRoute allowedRoles={['provider','agent']}><QRProfile /></ProtectedRoute>} />

            {/* Public */}
            <Route path="/asset/:assetId"    element={<PublicProfile />} />
            <Route path="/pet/:qrCodeId"     element={<PetPublicProfile />} />
            <Route path="/wholesale"         element={<WholesaleCatalog />} />

            {/* Fallback */}
            <Route path="*"                  element={<Navigate to="/" replace />} />
          </Routes>
        </>
      } />

    </Routes>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AdminAuthProvider>
          <ThemeProvider>
            <SiteSettingsProvider>
              <AppRoutes />
              <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                draggable
                pauseOnHover
                theme="dark"
              />
            </SiteSettingsProvider>
          </ThemeProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </Router>
  );
}

export default App;
